import { Button, LinearProgress, Paper, Typography, Stack, Alert } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArticleIcon from "@mui/icons-material/Article";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useParams } from "react-router-dom";
import { useI18n } from "../../../hooks/useI18n";
import { theme } from "../../../layout/Theme";
import { EmptyListPlaceholder } from "../../../components/shared/EmptyListPlaceholder";
import DocumentList from "./DocumentList";
import StyledDialog from "../../../components/mui/StyledDialog";
import { useState } from "react";
import { useServiceBus } from "../../../hooks/useServiceBus";
import { DocumentForm } from "./DocumentForm";
import { DeleteButton } from "../../../components/mui/StyledButtons";
import { useHealthRecordDocuments } from "../../../hooks/useHealthRecordDocuments";

function getMimeTypeFromFilename(filename: string): string {
  const extension = filename.toLowerCase().split(".").pop() || "";
  const mimeTypes: Record<string, string> = {
    pdf: "application/pdf",
    png: "image/png",
    jpeg: "image/jpeg",
    jpg: "image/jpeg",
    tiff: "image/tiff",
  };

  return mimeTypes[extension] || "application/octet-stream";
}

export function Documents() {
  const { t } = useI18n();
  const { id } = useParams() as { id: string };
  const serviceBus = useServiceBus();

  const { documents = [], isLoading, error: documentsError, refreshDocuments } = useHealthRecordDocuments(id);

  const [isAddDocumentOpen, setIsAddDocumentOpen] = useState(false);
  const [editingDocumentId, setEditingDocumentId] = useState<string | undefined>();
  const editingDocument = editingDocumentId ? documents.find((doc) => doc.id === editingDocumentId) : undefined;

  const [documentToDelete, setDocumentToDelete] = useState<string | null>(null);
  const [isDeletingDocument, setIsDeletingDocument] = useState<boolean>(false);

  const [attachmentToDelete, setAttachmentToDelete] = useState<{ fileId: string; documentId: string } | null>(null);
  const [isDeletingAttachment, setIsDeletingAttachment] = useState<{ fileId: string } | null>(null);

  const handleDownload = async (documentId: string, attachment: { fileId: string; filename: string }) => {
    try {
      const result = await serviceBus.dispatch({
        type: "downloadDocumentFile",
        healthRecordId: id,
        documentId: documentId,
        attachment: attachment,
      });
      if (result.type === "error") {
        throw new Error(result.message);
      }
      const url = window.URL.createObjectURL(result.value);
      const a = document.createElement("a");
      a.href = url;
      a.download = attachment.filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  const handleDeleteDocument = (documentId: string) => {
    setDocumentToDelete(documentId);
  };

  const confirmDeleteDocument = async () => {
    if (!documentToDelete) return;
    try {
      setIsDeletingDocument(true);
      await serviceBus.dispatch({
        type: "deleteDocument",
        healthRecordId: id,
        documentId: documentToDelete,
      });
    } catch (error) {
      console.error("Error deleting document:", error);
    } finally {
      setIsDeletingDocument(false);
      setDocumentToDelete(null);
      refreshDocuments({ hideLoading: true });
    }
  };

  const handleDeleteAttachment = ({ documentId, fileId }: { documentId: string; fileId: string }) => {
    setAttachmentToDelete({ documentId, fileId });
  };

  const confirmDeleteAttachment = async () => {
    if (!attachmentToDelete) return;
    try {
      setIsDeletingAttachment({ fileId: attachmentToDelete.fileId });
      await serviceBus.dispatch({
        type: "removeDocumentFiles",
        healthRecordId: id,
        documentId: attachmentToDelete.documentId,
        fileIds: [attachmentToDelete.fileId],
      });
    } catch (error) {
      console.error("Error deleting attachment:", error);
    } finally {
      setIsDeletingAttachment(null);
      setAttachmentToDelete(null);
      refreshDocuments({ hideLoading: true });
    }
  };

  const handlePreview = async (documentId: string, attachment: { fileId: string; filename: string }) => {
    try {
      const result = await serviceBus.dispatch({
        type: "downloadDocumentFile",
        healthRecordId: id,
        documentId: documentId,
        attachment: attachment,
      });
      if (result.type === "error") {
        throw new Error(result.message);
      }
      const mimeType = getMimeTypeFromFilename(attachment.filename);
      const blobUrl = URL.createObjectURL(new Blob([result.value], { type: mimeType }));
      window.open(blobUrl, "_blank");
    } catch (error) {
      console.error("Error previewing file:", error);
    }
  };

  const handleCloseAddDocument = () => {
    setIsAddDocumentOpen(false);
    refreshDocuments({ hideLoading: true });
  };

  const handleCloseEditDocument = () => {
    setEditingDocumentId(undefined);
    refreshDocuments({ hideLoading: true });
  };

  return (
    <Paper sx={{ overflow: "hidden", backgroundColor: "inherit" }} elevation={0}>
      {documentsError ? (
        <Typography sx={{ my: 4 }} align="center" component="div">
          <Alert severity="error">{t("common.errors.default")}</Alert>
        </Typography>
      ) : null}

      {isLoading ? (
        <LinearProgress variant="query" sx={{ my: 2 }} />
      ) : documents.length === 0 ? (
        <EmptyListPlaceholder
          icon={<ArticleIcon fontSize="large" sx={{ color: theme.palette.primary[500] }} />}
          title={t("documents.no_documents")}
          button={
            <Button
              size="large"
              onClick={() => setIsAddDocumentOpen(true)}
              variant="contained"
              startIcon={<AddIcon />}
              disableElevation
            >
              {t("documents.cta.add")}
            </Button>
          }
        >
          {t("documents.cta.add_msg")}
        </EmptyListPlaceholder>
      ) : (
        <div>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent={{ xs: "center", sm: "space-between" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            m={2}
          >
            <Typography sx={{ display: { xs: "none", sm: "block" } }}>{t("documents.title")}</Typography>
            <Button
              onClick={() => setIsAddDocumentOpen(true)}
              variant="contained"
              startIcon={<AddIcon />}
              disableElevation
            >
              {t("documents.cta.add")}
            </Button>
          </Stack>

          <DocumentList documents={documents} onDelete={handleDeleteDocument} onEdit={setEditingDocumentId} />
          <Alert severity="info" sx={{ my: 2, mx: { xs: 2, md: 0 } }}>
            {t("documents.info_msg")}
          </Alert>
        </div>
      )}
      <StyledDialog
        open={isAddDocumentOpen}
        onClose={handleCloseAddDocument}
        title={t("documents.add_document")}
        hideDialogActions
        maxWidth="sm"
      >
        <DocumentForm onClose={handleCloseAddDocument} documents={documents} />
      </StyledDialog>
      <StyledDialog
        open={!!editingDocument}
        onClose={handleCloseEditDocument}
        title={t("documents.edit_document")}
        hideDialogActions
        maxWidth="sm"
      >
        <DocumentForm
          onClose={handleCloseEditDocument}
          existingDocument={editingDocument}
          documents={documents}
          onDeleteAttachment={(fileId) => {
            handleDeleteAttachment({ documentId: editingDocumentId || "", fileId });
          }}
          onDownloadAttachment={handleDownload}
          onPreviewAttachment={handlePreview}
          isDeletingAttachment={isDeletingAttachment}
        />
      </StyledDialog>

      <StyledDialog
        open={documentToDelete !== null}
        onClose={() => setDocumentToDelete(null)}
        title={t("documents.delete_document")}
        maxWidth="sm"
        dialogActions={
          <DeleteButton
            onClick={confirmDeleteDocument}
            autoFocus
            startIcon={<DeleteOutlineIcon />}
            loading={isDeletingDocument}
          >
            {t("common.cta.delete")}
          </DeleteButton>
        }
      >
        {t("documents.delete_document_confirmation_message")}
      </StyledDialog>

      <StyledDialog
        open={attachmentToDelete !== null && isDeletingAttachment === null}
        onClose={() => setAttachmentToDelete(null)}
        title={t("documents.delete_attachment")}
        maxWidth="sm"
        dialogActions={
          <DeleteButton onClick={confirmDeleteAttachment} autoFocus startIcon={<DeleteOutlineIcon />}>
            {t("common.cta.delete")}
          </DeleteButton>
        }
      >
        {t("documents.delete_attachment_confirmation_message")}
      </StyledDialog>
    </Paper>
  );
}
